/**
 * useCountdownTimer Hook
 *
 * Manages countdown state for calendar events with smart interval updates
 * and optimized performance through memoization.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from "react";

import type { GeocodedEvent } from "@/types/calendar";
import type { CountdownConfig } from "@/utils/countdown.utils";
import {
  calculateCountdown,
  DEFAULT_COUNTDOWN_CONFIG,
} from "@/utils/countdown.utils";

export interface UseCountdownTimerOptions {
  events: GeocodedEvent[];
  enabled?: boolean;
  config?: Partial<CountdownConfig>;
}

export interface UseCountdownTimerReturn {
  eventsWithCountdowns: GeocodedEvent[];
  isUpdating: boolean;
  lastUpdateTime: Date | null;
  currentInterval: number;
}

/**
 * Hook to manage countdown timers for multiple events
 * with smart interval updates based on proximity
 */
export const useCountdownTimer = (
  options: UseCountdownTimerOptions,
): UseCountdownTimerReturn => {
  const { events, enabled = true, config: userConfig } = options;

  // Merge user config with defaults
  const config = useMemo(
    () => ({ ...DEFAULT_COUNTDOWN_CONFIG, ...userConfig }),
    [userConfig],
  );

  const [eventsWithCountdowns, setEventsWithCountdowns] = useState<
    GeocodedEvent[]
  >([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<Date | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [currentInterval, setCurrentInterval] = useState<number>(60000); // Default 1 minute

  /**
   * Calculate countdown data for all events
   */
  const updateCountdowns = useCallback(() => {
    if (!enabled) return;

    setIsUpdating(true);

    const updated = events.map((event) => {
      // Use nextOccurrence if available (for recurring events), otherwise use start
      const targetDate = event.nextOccurrence || event.start;
      const endDate = event.end;

      // Calculate countdown
      const countdownData = calculateCountdown(targetDate, endDate, config);

      return {
        ...event,
        countdownData,
      };
    });

    setEventsWithCountdowns(updated);
    setLastUpdateTime(new Date());
    setIsUpdating(false);
  }, [events, enabled, config]);

  /**
   * Set up interval updates - ALWAYS 1 SECOND for full timers
   */
  useEffect(() => {
    if (!enabled) {
      // Clear interval if disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial update
    updateCountdowns();

    // Always use 1 second interval for live ticking timers
    const interval = 1000; // 1 second
    setCurrentInterval(interval);

    // Set up interval - updates every second for live countdown
    intervalRef.current = setInterval(() => {
      updateCountdowns();
    }, interval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, updateCountdowns]);

  /**
   * Update when events array changes
   */
  useEffect(() => {
    if (enabled) {
      updateCountdowns();
    }
  }, [events, enabled, updateCountdowns]);

  return {
    eventsWithCountdowns,
    isUpdating,
    lastUpdateTime,
    currentInterval,
  };
};

/**
 * Hook to get countdown for a single event
 */
export const useEventCountdown = (
  event: GeocodedEvent | null,
  enabled = true,
) => {
  const events = useMemo(() => (event ? [event] : []), [event]);
  const { eventsWithCountdowns } = useCountdownTimer({ events, enabled });

  return eventsWithCountdowns[0] || null;
};
